import { useState, useCallback, useEffect } from 'react';
import api from '../api/api';
import { decryptKeyValue } from '../config/utility';

const DEFAULT_META = {
    total: 0,
    per_page: 15,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0,
};

const POLL_INTERVAL_MS = 60000;

export const useNotifications = ({ perPage = 15, autoFetchList = true } = {}) => {
    const roles = decryptKeyValue('roles') || [];
    const isOrganizer = roles.includes('organizer');

    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(autoFetchList);
    const [currentPage, setCurrentPage] = useState(1);
    const [meta, setMeta] = useState(DEFAULT_META);

    const fetchUnreadCount = useCallback(async () => {
        if (!isOrganizer) return;
        try {
            const response = await api.get('/notifications/unread-count');
            setUnreadCount(response.data?.count ?? 0);
        } catch (error) {
            console.error('Error fetching unread notification count:', error);
        }
    }, [isOrganizer]);

    const fetchNotifications = useCallback(async () => {
        if (!isOrganizer) return;
        setLoading(true);
        try {
            const response = await api.get('/notifications', {
                params: {
                    page: currentPage,
                    per_page: perPage,
                },
            });
            setNotifications(response.data || []);
            setMeta(response.meta || DEFAULT_META);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
            setIsInitialLoading(false);
        }
    }, [isOrganizer, currentPage, perPage]);

    const markAsRead = useCallback(async (id) => {
        if (!isOrganizer) return;
        try {
            await api.post(`/notifications/${id}/read`);
            const now = new Date().toISOString();
            setNotifications((prev) =>
                prev.map((n) =>
                    n.id === id ? { ...n, read_at: n.read_at || now } : n
                )
            );
            setUnreadCount((prev) => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    }, [isOrganizer]);

    const markAllAsRead = useCallback(async () => {
        if (!isOrganizer) return;
        try {
            await api.post('/notifications/mark-all-read');
            const now = new Date().toISOString();
            setNotifications((prev) =>
                prev.map((n) => ({ ...n, read_at: n.read_at || now }))
            );
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    }, [isOrganizer]);

    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
    }, []);

    useEffect(() => {
        if (!isOrganizer) return undefined;
        fetchUnreadCount();
        const intervalId = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [isOrganizer, fetchUnreadCount]);

    useEffect(() => {
        if (!isOrganizer || !autoFetchList) return;
        fetchNotifications();
    }, [isOrganizer, autoFetchList, fetchNotifications]);

    return {
        isOrganizer,
        notifications,
        unreadCount,
        loading,
        isInitialLoading,
        meta,
        currentPage,
        fetchNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        handlePageChange,
    };
};
